import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { useApiPatch } from '@sierralabs/core-ui';
import { DeltaType } from '@sierralabs/track-changes';
import { useTrackChanges } from '@sierralabs/track-changes-react';
import { useSiteContext } from '../../SiteContext';
import { useSendUpdatedComponent } from '../iframe-messaging/useSendUpdateComponent';
// type StringableKey<T> = T extends readonly unknown[]
//   ? number extends T['length']
//     ? number
//     : `${number}`
//   : string | number;
// type PathObject<T> = {
//   [P in keyof T & StringableKey<T>]: `${P}` | `${P}.${Path<T[P]>}`;
// };
// type Path<T> = T extends object
//   ? PathObject<T>[keyof T & StringableKey<T>]
//   : never;
// type StringableKey<T> = T extends readonly unknown[]
//   ? number
//   : string | number;
// type PathObject<T> = {
//   [P in keyof T]: T extends Array<infer E>
//     ? `{index}.${Path<T[P]>}`
//     : P | `${P & string}.${Path<T[P]>}`;
// };
// type Path<T> = T extends object
//   ? PathObject<T>[keyof T]
//   : never;
// type PathString<T extends object, P extends keyof T & string> = T extends Array<
//   infer E
// >
//   ? `{index}.${Path<E>}`
//   : P | `${P}.${Path<T[P]>}`;
// type Path<T> = T extends object ? PathString<T, keyof T & string> : never;
// type PathObject<T extends object, TKey1 extends keyof T,
// TKey2 extends keyof T[TKey1]
// > = [TKey1, TKey2];
// export type ComponentPath<T> = T extends object ?
// PathObject<T, keyof T>
// : never;
// // TKey3 extends keyof T[TKey1][TKey2]
/**
 * Updates the component in the component tree, notifies the iframe of the
 * update and saves to the server.
 * @param initialComponent
 */
export function useConfigComponent(initialComponent) {
    const siteContext = useSiteContext();
    const { replaceComponent, setUpdateComponentState, siteComponentMap } = siteContext;
    initialComponent = siteComponentMap[initialComponent.id];
    const siteContextRef = useRef(siteContext);
    const { plugins, setSelectedComponent } = siteContext;
    const pluginsRef = useRef(plugins);
    const timeoutRef = useRef();
    const trackChanges = useTrackChanges(initialComponent);
    const { sendUpdatedComponent } = useSendUpdatedComponent();
    const { isLoading: updatingComponent, error: updateComponentError, patch: applyDeltas } = useApiPatch(`components/${trackChanges.instance.id}`);
    // update siteContextRef if context updates
    useEffect(() => void (siteContextRef.current = siteContext), [siteContext]);
    // update pluginsRef if plugins update
    useEffect(() => void (pluginsRef.current = plugins), [plugins]);
    // reinitialize trackChanages if editing different component
    useEffect(() => {
        if (initialComponent.id !== trackChanges.instance.id) {
            // console.log('trackchanges restart');
            trackChanges.restart(initialComponent);
        }
    }, [initialComponent, trackChanges]);
    // update site context if applying component updates and any errors
    useEffect(() => setUpdateComponentState({ updatingComponent, updateComponentError }), [setUpdateComponentState, updateComponentError, updatingComponent]);
    const applyChanges = useCallback(() => {
        // send updated component on change
        const component = trackChanges.instance;
        // console.log('send component', component);
        sendUpdatedComponent(component);
        // debounce saving 500ms
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            const deltas = trackChanges.commit();
            const deltasCopy = deltas.map(delta => {
                // prevent circular dependency issue with serializing; since we are soft
                // deleting on the server side, we don't need to store the old component
                // JSON.
                if (delta.path.indexOf('children') === 0) {
                    const deltaCopy = Object.assign({}, delta);
                    if (delta.type === DeltaType.Delete) {
                        delete deltaCopy.oldValue;
                        return deltaCopy;
                    }
                    else if (delta.type === DeltaType.Insert) {
                        const removeParents = (component) => {
                            var _a;
                            const componentCopy = Object.assign({}, component);
                            delete componentCopy.parent;
                            componentCopy.children = (_a = componentCopy.children) === null || _a === void 0 ? void 0 : _a.map(child => removeParents(child));
                            return componentCopy;
                        };
                        deltaCopy.newValue = removeParents(deltaCopy.newValue);
                        return deltaCopy;
                    }
                }
                return delta;
            });
            console.log('apply deltas', deltasCopy);
            applyDeltas(deltasCopy);
            // replace component from tree with updated values
            replaceComponent(component);
        }, 500);
        setSelectedComponent(component);
    }, [
        trackChanges,
        replaceComponent,
        sendUpdatedComponent,
        setSelectedComponent,
        applyDeltas
    ]);
    // const update = useCallback(
    //   <K extends keyof Component<T>>(key: K, value: Component<T>[K]) => {
    //     trackChanges.update(key, value);
    //     applyChanges();
    //   },
    //   [trackChanges, applyChanges]
    // );
    /**
     * Update a component baesd on its props string path dot notation
     * Note: If updating an array in the props use the `trackChanges`
     * API. The props string path validation doesn't handle dynamic
     * array indexes.
     */
    const update = useCallback((path, value) => {
        if (path.indexOf('type') === 0)
            throw new Error('Please use changeType() to update component type');
        trackChanges.update(path, value);
        applyChanges();
    }, [trackChanges, applyChanges]);
    // const update = useCallback(
    //   <
    //     TObject extends T,
    //     TKey1 extends keyof Component<TObject>,
    //     TKey2 extends keyof Component<TObject>[TKey1],
    //     TKey3 extends keyof Component<TObject>[TKey1][TKey2],
    //     TKey4 extends keyof Component<TObject>[TKey1][TKey2][TKey3],
    //     TKey5 extends keyof Component<TObject>[TKey1][TKey2][TKey3][TKey4]
    //   >(
    //     path: [TKey1, TKey2, TKey3, TKey4, TKey5],
    //     value: any
    //   ) => {
    //     trackChanges.update(path, value);
    //     applyChanges();
    //   },
    //   [trackChanges, applyChanges]
    // );
    const insert = useCallback((path, value) => {
        if (path.indexOf('children') === 0)
            throw new Error('Please use insertChild() to insert a child component');
        trackChanges.insert(path, value);
        applyChanges();
    }, [trackChanges, applyChanges]);
    const remove = useCallback((path) => {
        if (path.indexOf('children') === 0)
            throw new Error('Please use removeChild() to remove a child component');
        trackChanges.delete(path);
        applyChanges();
    }, [trackChanges, applyChanges]);
    const move = useCallback((path, sourceIndex, destinationIndex) => {
        if (path.indexOf('children') === 0)
            throw new Error('Please use moveChild() to move a child component');
        trackChanges.move(path, sourceIndex, destinationIndex);
        applyChanges();
    }, [trackChanges, applyChanges]);
    // const updateProps = useCallback(
    //   <
    //     K1 extends keyof T,
    //     I1 extends T[K1] extends (infer E)[] ? number : T[K1],
    //     V1 extends T[K1] extends (infer E)[] ? E : T[K1],
    //     K2 extends I1 ? number : keyof T : ,
    //     // I2 extends T[K2] extends (infer E)[] ? number : T[K1],
    //     // V2 extends T[K2] extends (infer E)[] ? E : T[K1]
    //   >(
    //     key: K1
    //   ) => {
    //     // trackChanges.update(`props.${key}`, value);
    //   },
    //   [trackChanges]
    // );
    // const updatePropsArray = useCallback(
    //   <K extends keyof T>(key: K, index: number, value: T[K]) => {
    //     trackChanges.update(`props.${key}[index]`, value);
    //   },
    //   [trackChanges]
    // );
    // const insertComponentPropsArray = useCallback(
    //   <K extends keyof T, V extends T[K]>(
    //     key: K,
    //     value: V extends (infer E)[] ? E : V // infer the item in array
    //   ) => {
    //     trackChanges.insert(`props.${key}`, value);
    //   },
    //   [trackChanges]
    // );
    // needed for recursive call from insertChild()
    const cleanupChildPluginSlotsRef = useRef(null);
    /**
     * Insert a child component with options to insert deep nested components.
     */
    const insertChild = useCallback((type, name, options) => {
        const path = (options === null || options === void 0 ? void 0 : options.path) || 'children';
        // path must end with 'children'
        if (!/children$/.test(path)) {
            throw new Error('insertChild() options path must point to the component children array');
        }
        const parent = path === 'children'
            ? trackChanges.instance
            : trackChanges.get(path.slice(0, -'.children'.length));
        if (!trackChanges.get(path)) {
            trackChanges.update(path, []);
        }
        const { slot, props = {} } = options || {};
        let childComponent = {
            id: uuid(),
            name,
            type,
            slot,
            props,
            parent,
            depth: parent.depth + 1,
            position: parent.children.length,
            children: []
        };
        siteComponentMap[childComponent.id] = childComponent;
        const plugin = pluginsRef.current.find(p => p.type === type);
        if (plugin === null || plugin === void 0 ? void 0 : plugin.initConfig) {
            childComponent = plugin.initConfig(childComponent, siteContextRef.current);
        }
        trackChanges.insert(path, childComponent);
        console.log('insert child', childComponent);
        // console.log('trackChanges.instance.', trackChanges.instance);
        const index = trackChanges.get(path).length - 1;
        cleanupChildPluginSlotsRef.current(index, `${path}[${index}]`);
        applyChanges();
    }, [trackChanges, siteComponentMap, applyChanges]);
    const moveChild = useCallback((sourceIndex, destinationIndex) => {
        trackChanges.move('children', sourceIndex, destinationIndex);
        const children = trackChanges.instance.children || [];
        for (let index = 0; index < children.length; index++) {
            // increment position update on frontend, while server side will
            // implement position change on save.
            children[index].position = index;
        }
        applyChanges();
    }, [trackChanges, applyChanges]);
    const removeChild = useCallback((index) => {
        trackChanges.delete(`children[${index}]`);
        applyChanges();
    }, [trackChanges, applyChanges]);
    const updateSlot = useCallback((slot, type) => {
        var _a;
        const plugin = pluginsRef.current.find(p => p.type === type);
        if (!plugin)
            throw new Error(`Plugin "${type}" not found`);
        if (!trackChanges.instance.children) {
            // console.log('adding children');
            trackChanges.update('children', []);
        }
        let index = (_a = trackChanges.instance.children) === null || _a === void 0 ? void 0 : _a.findIndex(c => c.slot === slot);
        // console.log('index', index, trackChanges.instance);
        if (typeof index === 'undefined' || index === -1) {
            // console.log('adding component to children');
            insertChild(type, plugin.name, { slot });
        }
        else {
            trackChanges.update(`children[${index}].name`, plugin.name);
            trackChanges.update(`children[${index}].type`, type);
            trackChanges.delete(`children[${index}].props`);
            cleanupChildPluginSlotsRef.current(index);
            applyChanges();
        }
        // console.log('updated trackChanges.instance', trackChanges.instance);
    }, [trackChanges, insertChild, applyChanges]);
    /**
     * when changing component type clean up slots. path if provided, should be
     * the path to the component children array.
     */
    const cleanPluginSlots = useCallback((plugin, path) => {
        const component = path
            ? trackChanges.get(path)
            : trackChanges.instance;
        if (!path)
            path = 'children';
        if (plugin.slots) {
            if (!component.children) {
                trackChanges.update(path, []);
                component.children = [];
            }
            for (const slot of plugin.slots) {
                if (!component.children.some(c => c.slot === slot.slot)) {
                    // add slot and recursively check newly added child
                    insertChild(slot.type, slot.name, {
                        path,
                        slot: slot.slot,
                        props: slot.props
                    });
                }
            }
        }
        else if (component.children) {
            // remove any existing slots
            for (let i = component.children.length - 1; i >= 0; i--) {
                if (component.children[i].slot) {
                    trackChanges.delete(`${path}[${i}]`);
                }
            }
        }
    }, [insertChild, trackChanges]);
    /**
     * When adding a child component check if slots need to be added or removed
     */
    const cleanupChildPluginSlots = useCallback((index, path) => {
        if (!path)
            path = `children[${index}]`;
        const childComponent = trackChanges.get(path);
        path += '.children';
        const plugin = pluginsRef.current.find(p => p.type === childComponent.type);
        if (!plugin)
            console.log('cannot find plugin', childComponent.type);
        cleanPluginSlots(plugin, path);
    }, [cleanPluginSlots, trackChanges]);
    cleanupChildPluginSlotsRef.current = cleanupChildPluginSlots;
    /**
     * Change type of component or child component.
     */
    const changeType = useCallback((type, path) => {
        const plugin = pluginsRef.current.find(p => p.type === type);
        if (!plugin)
            throw new Error(`Plugin "${type}" not found`);
        path = path ? `${path}.` : '';
        console.log('path', path);
        console.log('updating to type', type);
        trackChanges.update(`${path}name`, plugin.name);
        trackChanges.update(`${path}type`, type);
        trackChanges.update(`${path}props`, {});
        console.log('changeType trackChanges.instance', trackChanges.instance);
        cleanPluginSlots(plugin, path ? `${path}children` : path);
        applyChanges();
    }, [applyChanges, cleanPluginSlots, trackChanges]);
    return {
        trackChanges,
        component: trackChanges.instance,
        update,
        insert,
        delete: remove,
        remove,
        move,
        changeType,
        updateSlot,
        insertChild,
        removeChild,
        moveChild
    };
}
// const normalizePathValue = (
//   path: Path<Component<T>>,
//   valueOrIndex: any,
//   value?: any
// ) => {
//   if (value && Array.isArray(valueOrIndex)) {
//     valueOrIndex.forEach(
//       index => (path = path.replace('{index}', index) as Path<T>)
//     );
//   } else {
//     value = valueOrIndex;
//   }
//   return { path, value };
// };
//# sourceMappingURL=useConfigComponent.js.map